import { useEffect, useRef, useState } from 'react';
import { previewRequestInstanceCharge } from '../services/Payments';
import { ChargeBreakdownAmounts, formatChargeCents } from '../components/ChargeBreakdown';
import { RequestChargePreviewResponse } from '../types/Payments';

export type ShownChargePreview = {
    key: string;
    requiresPayment: boolean;
    breakdown: ChargeBreakdownAmounts | null;
    capApplied: boolean;
};

export type RequestPaymentConsent = {
    amountDisplayedCents: number;
    confirmedAt: number;
    source: string;
};

const toShownChargePreview = (key: string, preview: RequestChargePreviewResponse): ShownChargePreview => {
    const charge = preview.chargeBreakdown;
    const totalCents = Number(charge && charge.totalCents);
    if (!preview.requiresPayment || !charge || !Number.isFinite(totalCents)) {
        return { key, requiresPayment: Boolean(preview.requiresPayment), breakdown: null, capApplied: false };
    }
    return {
        key,
        requiresPayment: true,
        breakdown: {
            currency: charge.currency,
            subtotalCents: Number(charge.subtotalCents) || 0,
            taxCents: Number(charge.taxCents) || 0,
            totalCents,
            taxPercent: charge.chargeComponents && charge.chargeComponents.taxPercent,
        },
        capApplied: Boolean(charge.capApplied),
    };
};

interface UseRequestChargePreviewArgs {
    // Only preview when this order will actually be charged (facility payments on and
    // the service is payable).
    paymentRequired: boolean;
    facilityId: string;
    residentId: string;
    serviceId: string;
    addOnIds: string[];
}

// Server-side charge preview for a new service request, shared by the order wizards.
// F15: the preview shown to the resident is the one recorded as consent on submit.
const useRequestChargePreview = ({ paymentRequired, facilityId, residentId, serviceId, addOnIds }: UseRequestChargePreviewArgs) => {
    const [chargePreview, setChargePreview] = useState<ShownChargePreview | null>(null);
    const [chargePreviewLoading, setChargePreviewLoading] = useState<boolean>(false);
    const chargePreviewSeqRef = useRef(0);

    const addOnKey = addOnIds.map(String).sort().join(',');
    const chargePreviewKey = [residentId, serviceId, addOnKey].join('|');
    const shownChargePreview = chargePreview && chargePreview.key === chargePreviewKey ? chargePreview : null;

    const fetchPreview = () => previewRequestInstanceCharge({
        facilityId,
        residentId,
        originalServiceId: serviceId,
        serviceAddOns: addOnIds.map((id) => ({ id: String(id) })),
    });

    useEffect(() => {
        if (!paymentRequired || !serviceId || !residentId) {
            setChargePreview(null);
            setChargePreviewLoading(false);
            return undefined;
        }
        const seq = chargePreviewSeqRef.current + 1;
        chargePreviewSeqRef.current = seq;
        setChargePreviewLoading(true);
        const timer = setTimeout(async () => {
            try {
                const preview = await fetchPreview();
                if (chargePreviewSeqRef.current !== seq) return;
                setChargePreview(toShownChargePreview(chargePreviewKey, preview));
            } catch (_error) {
                if (chargePreviewSeqRef.current !== seq) return;
                setChargePreview(null);
            } finally {
                if (chargePreviewSeqRef.current === seq) setChargePreviewLoading(false);
            }
        }, 400);
        return () => clearTimeout(timer);
        // chargePreviewKey covers the resident, service and add-on selection.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paymentRequired, chargePreviewKey, facilityId]);

    // Returns the consent to send with the order, or undefined when nothing is charged.
    // Captures what the resident was shown so a later price increase (before this request
    // is closed) is blocked server-side instead of silently charging more -- see
    // requestBilling.js's findConsentCeilingBreach, which only applies when this consent is
    // present. If the shown preview is missing or stale, fetch it, show it, and throw so
    // the resident reviews the total and submits again.
    const resolvePaymentConsent = async (source: string): Promise<RequestPaymentConsent | undefined> => {
        if (!paymentRequired) return undefined;
        let shown = shownChargePreview;
        if (!shown) {
            shown = toShownChargePreview(chargePreviewKey, await fetchPreview());
            setChargePreview(shown);
            if (shown.requiresPayment) {
                if (!shown.breakdown) {
                    throw new Error('Unable to preview payment amount for this request');
                }
                throw new Error(`The total for this request is ${formatChargeCents(shown.breakdown.totalCents, shown.breakdown.currency)}. Review it and submit again.`);
            }
        }
        if (!shown.requiresPayment) return undefined;
        const amountCents = Number(shown.breakdown && shown.breakdown.totalCents);
        // A $0 total (e.g. free add-ons) is still sent, so it caps the charge at close.
        if (!shown.breakdown || !Number.isFinite(amountCents) || amountCents < 0) {
            throw new Error('Unable to preview payment amount for this request');
        }
        return {
            amountDisplayedCents: Math.round(amountCents),
            confirmedAt: Date.now(),
            source,
        };
    };

    return { shownChargePreview, chargePreviewLoading, resolvePaymentConsent };
};

export default useRequestChargePreview;
